import React, { ErrorInfo, FC, useContext, useMemo, useState, useEffect, Component } from 'react';
import { FormContext, registry } from '..';
import json_pointer from 'json-pointer';
import { VISIBILITY_PATH_STARTING_INDEX } from '../constants';
import { of } from 'rxjs';
import { Button, Typography } from '@material-ui/core';
import { distinctUntilChanged } from 'rxjs/operators';

export interface LayoutNodeProps {
  path: string;
  /**
   * If layout node is in a table cell, remove its margin and padding
   */
  tableCell?: boolean;
}

// todo error boundary
export const LayoutNode: FC<LayoutNodeProps> = React.memo((props) => {
  const { form, visibilityMgr, isDeskAudit, isFocusedAudit, expMgr, scope } = useContext(FormContext);

  const definition = useMemo(function() {
    try {
      return json_pointer.get(form.definition, props.path)
    } catch(err) {
      console.log('error getting pointer to path "', props.path, '":', (err as any).message);
      return {};
    }
  }, [props.path, form.id, form.definition]);

  let [isVisible, setIsVisible] = useState(true);

  useEffect(function r() {
    const condition = definition.visibility ?? definition.visible;

    let observable;

    /**
     * A scope indicates that there is a nested <Form> element in a new context (a subform), so the initial visibility manager registry won't
     * contain the paths that need to be observed. Thus a new target must be watched in the expression manager.
     */
    if (scope) {
      observable = expMgr!.watch(condition, scope);
    } else {
      observable = visibilityMgr!.watch(props.path.slice(VISIBILITY_PATH_STARTING_INDEX));
    }

    const a = (condition !== undefined && definition.type !== 'Dismissible' && observable
      ? observable.pipe(distinctUntilChanged())
      : of(true))
      // take 1
        .subscribe((isVisible: any) => {
          if (isDeskAudit && definition.hasOwnProperty('auditable') && !definition.auditable) {
            return setIsVisible(false);
          }
          if (!isDeskAudit && definition.hasOwnProperty('auditOnly') && definition.auditOnly) {
            return setIsVisible(false);
          }
          if (isFocusedAudit && definition.hasOwnProperty('auditable') && !definition.auditable) {
            return setIsVisible(false);
          }
          if (!isFocusedAudit && definition.hasOwnProperty('auditOnly') && definition.auditOnly) {
            return setIsVisible(false);
          }
          setIsVisible(isVisible);
        });
    return function() {
      return a.unsubscribe();
    }
  }, [definition, isDeskAudit, isFocusedAudit, props.path, visibilityMgr, scope, expMgr]);

  if (isVisible) {
    var component = registry.getComponent(definition.type);

    if (!component) {
      throw new Error('No such control ' + definition.type + ' exists');
    }

    return React.createElement(component, {
      path: props.path,
      definition: {...definition, tableCell: props.tableCell},
    })
  } else {
    return null;
  }
}, (p, n) => {
  return p.path === n.path;
})

export interface LayoutNodeWrapperProps {
  error?: Error; info?: ErrorInfo;
}

export class LayoutNodeWrapper extends Component<LayoutNodeProps & LayoutNodeWrapperProps> {

  state: LayoutNodeWrapperProps = {};

  constructor(props: LayoutNodeProps & LayoutNodeWrapperProps) {
    super(props);
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ error, info });
  }

  render() {
    if (this.state.error !== undefined) {
      return <div>
        <Typography>Could not render control at {this.props.path + '\n' + this.state.error }</Typography>
        <Button
          variant = { 'text' }
          onClick = {() =>
            this.setState({
              error: undefined,
              info: undefined
            })}>
          Reload
        </Button>
      </div>
    }
    return <LayoutNode {...this.props}></LayoutNode>
  };
}
